import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterFetchDashboard, logoutMaster, getMasterAdmin } from '../lib/master-api';

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;
const formatDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('ko-KR');
};

const StatCard = ({ label, value, delta, color, icon }) => (
  <div style={{
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 20,
    position: 'relative',
    overflow: 'hidden',
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
    }}>
      <div style={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>{label}</div>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: `${color}15`,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>{icon}</div>
    </div>
    <div style={{
      fontSize: '1.55rem',
      fontWeight: 800,
      color: 'var(--text)',
      letterSpacing: '-0.5px',
    }}>
      {value}
    </div>
    {delta !== undefined && (
      <div style={{
        marginTop: 4,
        fontSize: '0.78rem',
        fontWeight: 600,
        color: delta >= 0 ? 'var(--success)' : 'var(--danger)',
      }}>
        {delta >= 0 ? '+' : ''}{delta}
      </div>
    )}
  </div>
);

const DonutChart = ({ data }) => {
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  if (total === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
        데이터가 없습니다
      </div>
    );
  }
  const colors = ['#BF9B30', '#00BF40', '#0EA5E9', '#FF9500', '#F04452'];
  let cumulative = 0;
  const segments = data.map((d, i) => {
    const pct = (d.count / total) * 100;
    const start = cumulative;
    cumulative += pct;
    return {
      ...d,
      pct,
      color: colors[i % colors.length],
      start,
      end: cumulative,
    };
  });

  const gradient = `conic-gradient(${segments
    .map((s) => `${s.color} ${s.start}% ${s.end}%`)
    .join(', ')})`;

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
      <div style={{
        width: 140,
        height: 140,
        borderRadius: '50%',
        background: gradient,
        position: 'relative',
        flexShrink: 0,
      }}>
        <div style={{
          position: 'absolute',
          inset: 24,
          background: '#fff',
          borderRadius: '50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text)' }}>{total}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>매장</div>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        {segments.map((s) => (
          <div key={s.plan_id} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 0',
            fontSize: '0.82rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
              <span style={{ color: 'var(--text)' }}>{s.plan_name}</span>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <span style={{ color: 'var(--text-tertiary)' }}>{s.count}</span>
              <span style={{ color: 'var(--text)', fontWeight: 700, width: 40, textAlign: 'right' }}>
                {s.pct.toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarChart = ({ data, valueKey = 'revenue', labelKey = 'month' }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
        데이터가 없습니다
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-end',
      gap: 16,
      height: 180,
      padding: '16px 8px',
    }}>
      {data.map((d, i) => {
        const h = ((d[valueKey] || 0) / max) * 100;
        return (
          <div key={i} style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}>
            <div style={{
              fontSize: '0.7rem',
              color: 'var(--text-secondary)',
              fontWeight: 600,
            }}>
              {formatWon(d[valueKey] || 0).replace('₩', '').replace(/,\d{3}$/, 'k')}
            </div>
            <div style={{
              width: '100%',
              height: `${h}%`,
              minHeight: 4,
              background: 'linear-gradient(180deg, #BF9B30 0%, #A07D1A 100%)',
              borderRadius: '6px 6px 0 0',
              transition: 'height 0.4s',
            }} />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
};

const MasterDashboardPage = () => {
  const navigate = useNavigate();
  const admin = getMasterAdmin();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await masterFetchDashboard();
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#F7F9FC' }}>
      {/* Top bar */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid var(--border)',
        padding: '14px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36,
            height: 36,
            background: 'linear-gradient(135deg, #BF9B30 0%, #806413 100%)',
            borderRadius: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
              <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '0.68rem', color: 'var(--primary)', fontWeight: 700, letterSpacing: 1 }}>
              MASTER CONSOLE
            </div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>QR Order Platform</div>
          </div>
        </div>
        <nav style={{ display: 'flex', gap: 6 }}>
          {[
            { label: '대시보드', to: '/master' },
            { label: '매장', to: '/master/stores' },
            { label: '구독', to: '/master/subscriptions' },
          ].map((item) => (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              style={{
                padding: '8px 14px',
                background: window.location.pathname === item.to ? 'var(--primary-light)' : 'transparent',
                color: window.location.pathname === item.to ? 'var(--primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: 8,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{admin?.name || '관리자'}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{admin?.email}</div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={logoutMaster}>
            로그아웃
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.5px' }}>대시보드</h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem', marginTop: 4 }}>
            플랫폼 주요 지표 및 최근 활동
          </p>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
            <div className="spinner spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
          </div>
        ) : (
          <>
            {/* Stats grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 16,
              marginBottom: 24,
            }}>
              <StatCard
                label="활성 매장"
                value={data.stats.total_stores}
                color="#BF9B30"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
              />
              <StatCard
                label="이번 달 신규"
                value={`+${data.stats.new_stores_this_month}`}
                color="#00BF40"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
              />
              <StatCard
                label="MRR"
                value={formatWon(data.stats.mrr)}
                color="#0EA5E9"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
              />
              <StatCard
                label="이번 달 매출"
                value={formatWon(data.stats.revenue_this_month)}
                color="#BF9B30"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
              />
              <StatCard
                label="미납 청구서"
                value={`${data.stats.pending_invoices_count}건`}
                color="#FF9500"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
              />
              <StatCard
                label="체험 중"
                value={data.stats.trial_subscriptions}
                color="#0EA5E9"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
              />
            </div>

            {/* Charts row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: 16,
              marginBottom: 24,
            }}>
              <div style={{
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: 24,
              }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase' }}>
                    월별 매출
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)' }}>
                    최근 6개월
                  </div>
                </div>
                <BarChart data={data.monthly_revenue} valueKey="revenue" labelKey="month" />
              </div>

              <div style={{
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: 24,
              }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase' }}>
                    플랜별 분포
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)' }}>
                    활성 구독
                  </div>
                </div>
                <DonutChart data={data.plan_distribution} />
              </div>
            </div>

            {/* Recent */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}>
              <div style={{
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: 24,
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>최근 가입 매장</div>
                {data.recent_stores.length === 0 ? (
                  <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>없음</div>
                ) : (
                  <div>
                    {data.recent_stores.map((s) => (
                      <div key={s.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '10px 0',
                        borderBottom: '1px solid var(--border)',
                        fontSize: '0.85rem',
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{s.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{s.owner_email}</div>
                        </div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>
                          {formatDate(s.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: 24,
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}>최근 활동</div>
                {data.recent_activity.length === 0 ? (
                  <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>활동 없음</div>
                ) : (
                  <div>
                    {data.recent_activity.map((a) => (
                      <div key={a.id} style={{
                        padding: '10px 0',
                        borderBottom: '1px solid var(--border)',
                        fontSize: '0.82rem',
                      }}>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                          {a.action}
                          {a.target_type && ` · ${a.target_type}`}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                          {a.admin_email || '시스템'} · {formatDate(a.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MasterDashboardPage;
